import { L1Object } from 'core/l1Object/L1Object';
import { DefaultL1ObjectManager } from 'core/l1Object/L1ObjectManager';
import { BidStrategy, L2ObjectOptimizationGoal } from 'core/l2Object/L2Object';
import { Order } from 'core/order/Order';
import {
  CampaignState,
  RtbCampaignListBasic
} from 'core/rtbCampaign/RtbCampaign';
import { DefaultRtbCampaignManager } from 'core/rtbCampaign/RtbCampaignManager';
import { getPriceValue } from 'helper/CurrencyHelper';
import i18n from 'i18n';
import _, { defaultTo } from 'lodash';
import moment from 'moment';
import { L1ObjectFormChannelModel } from './L1ObjectFormChannelModel';

export abstract class L1ObjectEditFormChannelModel implements L1ObjectFormChannelModel {
  minBudgetPerDay = 0;
  minBudgetPerCampaign = 0;
  constructor(
    protected order: Order,
    public l1Object: L1Object,
    protected l1ObjectManager = new DefaultL1ObjectManager()
  ) {
    this.minBudgetPerDay = order.campaignConstraint.budgetMinimum;
    this.minBudgetPerCampaign = order.campaignConstraint.campaignBudgetMinimum;
  }

  abstract minBudget(values: Partial<L1Object>);

  abstract get totalBudget();

  abstract init(callback: (data) => void);

  abstract validate(value: Partial<L1Object>);

  abstract getCboChangeHint();

  abstract getDefaultBidStrategy();

  abstract getBudgetManagementModalData(
    values: Partial<L1Object>,
    valueUpdater: (updateFunc: (prevValue: any) => any) => void
  ): any;

  abstract onCBOChange(
    enable: boolean,
    valueUpdater: (updateFunc: (prevValue: any) => any) => void,
    showManagementModal: () => void
  );

  abstract showRemainBudget(values: Partial<L1Object>);

  abstract toChannelNativeData(l1Object: L1Object, extraData: any): any;
}

export class DefaultL1ObjectEditFormModel extends L1ObjectEditFormChannelModel {
  campaignsWithDailyBudget: RtbCampaignListBasic[];

  constructor(
    order: Order,
    l1Object: L1Object,
    private rtbCampaigns?: RtbCampaignListBasic[],
    private campaignManager = new DefaultRtbCampaignManager()
  ) {
    super(order, l1Object);
    this.campaignsWithDailyBudget = [];
  }

  minBudget(values: Partial<L1Object>) {
    const order = this.order;
    // 查看編輯當下選擇的 CBO 狀態
    const isCBO: boolean = _.get(values, 'autoOptimise', false);
    const managementBudgets = _.get(values, 'rtb.campaign_budgets', []);
    // CBO 時 inactive 的廣告活動就略過預算檢查
    const ignoredCampaignStates = isCBO
      ? [CampaignState.DELETE, CampaignState.DEACTIVATE]
      : [CampaignState.DELETE];
    const validRtbCampaigns = this.rtbCampaigns
      ? this.rtbCampaigns.filter(
          (campaign) => !_.includes(ignoredCampaignStates, campaign.state)
        )
      : [];
    if (validRtbCampaigns.length === 0) {
      return this.minBudgetPerDay;
    } else {
      const minBudgetOfRtbCampaign = validRtbCampaigns.reduce(
        (acc, rtbCampaign) => {
          // CBO 時的廣告活動檢查邏輯為：考慮廣告活動的日預算 * 天數
          // 非 CBO 時的廣告活動檢查邏輯為：考慮廣告活動的預算
          const managementBudget = managementBudgets.find(
            (campaign) => campaign.id === rtbCampaign.id?.toString()
          );
          const campaignBudget = managementBudget
            ? managementBudget.budget
            : rtbCampaign.budget;
          const minBudget = isCBO
            ? Math.max(
                this.campaignManager.getMinBudgetOfCampaign(
                  rtbCampaign,
                  this.minBudgetPerDay
                ),
                rtbCampaign.expectedSpent
              )
            : campaignBudget;
          return acc + minBudget;
        },
        0
      );
      return Math.min(
        order.budget,
        getPriceValue(order.currency, minBudgetOfRtbCampaign)
      );
    }
  }

  get totalBudget() {
    if (!this.l1Object || !this.rtbCampaigns) {
      return 0;
    }
    let l1ObjectBudget = 0;
    const currentFieldValue = _.get(this.l1Object, 'budget');
    if (currentFieldValue !== '' && currentFieldValue !== undefined) {
      l1ObjectBudget = +_.defaultTo(currentFieldValue, 0).toString();
    } else {
      l1ObjectBudget = this.rtbCampaigns.reduce((acc, rtbCampaign) => {
        return rtbCampaign.budget ? acc + +rtbCampaign.budget : acc;
      }, 0);
    }
    return _.defaultTo(_.get(this.order, 'budgetBalance'), 0) + l1ObjectBudget;
  }

  init = async (callback: (data) => void) => {
    if (!this.rtbCampaigns) {
      this.rtbCampaigns = await this.campaignManager.getCampaignsOfGroup(
        this.l1Object.l1ObjectId
      );
    }
    this.campaignsWithDailyBudget =
      await this.campaignManager.getRtbCampaignsUsingDailyBudget(
        this.l1Object.l1ObjectId
      );
    callback({
      l2ObjectList: this.rtbCampaigns
    });
  };

  validate = (value: Partial<L1Object>) => {
    // 查看編輯當下選擇的 CBO 狀態
    const isCBO: boolean = _.get(value, 'autoOptimise', false);
    const isSwitchingCBO: boolean = isCBO !== this.l1Object.autoOptimise;
    if (isSwitchingCBO && isCBO) {
      // 從非 CBO 切換到 CBO 時，檢查底下廣告活動預算是否大於日預算 * 天數
      const invalidRtbCampaignIds = this.rtbCampaigns
        ? this.rtbCampaigns
            .filter((campaign) => {
              const minBudget = this.campaignManager.getMinBudgetOfCampaign(
                campaign,
                this.minBudgetPerDay
              );
              return campaign.budget < minBudget;
            })
            .map((campaign) => campaign.id)
        : [];
      if (invalidRtbCampaignIds.length > 0) {
        return {
          autoOptimise: i18n.t('l1Object.labels.cboRtbCampaignBudgetLimit', {
            campaignIds: invalidRtbCampaignIds.join(', ')
          })
        };
      }
    }
    return {};
  };

  getCboChangeHint = () => {
    if (
      this.l1Object &&
      this.l1Object.autoOptimise &&
      this.rtbCampaigns &&
      this.rtbCampaigns.length === 0
    ) {
      return i18n.t('l1Object.labels.cboRtbCampaignLengthLimit');
    }
    const cboOpenLimit = this.getCboOpenLimit();
    if (cboOpenLimit && !this.l1Object.autoOptimise) {
      return cboOpenLimit;
    }

    const lastBudgetTogglingTime = _.get(this.l1Object, 'rtb.update_cbo_time');
    if (lastBudgetTogglingTime) {
      const minutes = moment(lastBudgetTogglingTime)
        .add(2, 'hours')
        .diff(moment(), 'minutes');
      if (minutes > 0) {
        return i18n.t('l1Object.labels.cboTimeLimit', {
          time: minutes
        });
      }
    }
  };

  getCboOpenLimit() {
    if (!this.rtbCampaigns || this.rtbCampaigns.length === 0) {
      return;
    }
    const optimizationGoals: L2ObjectOptimizationGoal[] = [];
    const bidStrategies: BidStrategy[] = [];
    this.rtbCampaigns.forEach((rtbCampaign) => {
      optimizationGoals.push(rtbCampaign.optimize);
      bidStrategies.push(
        _.isNumber(rtbCampaign.bidPrice)
          ? BidStrategy.LOWEST_COST_WITH_BID_CAP
          : BidStrategy.LOWEST_COST_WITHOUT_CAP
      );
    });
    if (
      _.uniq(optimizationGoals).length > 1 ||
      _.uniq(bidStrategies).length > 1
    ) {
      return i18n.t('l1Object.labels.rtbCboLimitation');
    }
    if (
      this.campaignsWithDailyBudget &&
      this.campaignsWithDailyBudget.length > 0
    ) {
      return i18n.t('l1Object.labels.rtbCboDailyBudgetHint');
    }
  }

  getDefaultBidStrategy() {
    // 只能用出價上限的渠道不必反推——群組底下 0 張活動時反推會落到自動出價，而那個值
    // 會被後續建立的活動整批繼承。
    if (this.l1ObjectManager.isCboBidCapOnlyChannel(this.l1Object.channel)) {
      return BidStrategy.LOWEST_COST_WITH_BID_CAP;
    }
    if (!this.rtbCampaigns || this.rtbCampaigns.length === 0) {
      return BidStrategy.LOWEST_COST_WITHOUT_CAP;
    }
    return _.isNumber(this.rtbCampaigns[0].bidPrice)
      ? BidStrategy.LOWEST_COST_WITH_BID_CAP
      : BidStrategy.LOWEST_COST_WITHOUT_CAP;
  }

  getBudgetManagementModalData(
    values: Partial<L1Object>,
    valuesUpdater: (updateFunc: (prevValue: any) => any) => void
  ) {
    return {
      budgetDataList: _.get(values, 'rtb.campaign_budgets'),
      order: this.order,
      l1Object: values,
      initL1Object: this.l1Object,
      l2ObjectList: this.rtbCampaigns,
      onSubmit: (budgetMap) => {
        valuesUpdater((prev) => ({
          ...prev,
          autoOptimise: false,
          rtb: {
            ...defaultTo(prev.rtb, {}),
            bid_strategy: undefined,
            campaign_budgets: Object.keys(budgetMap).map((id) => ({
              id: id,
              budget: budgetMap[id]
            }))
          }
        }));
      },
      onCancel: () => {
        if (!_.get(values, 'rtb.campaign_budgets')) {
          valuesUpdater((prev) => ({
            ...prev,
            autoOptimise: true
          }));
        }
      }
    };
  }

  onCBOChange(
    enable: boolean,
    valuesUpdater: (updateFunc: (prevValue: any) => any) => void,
    showManagementModal: () => void
  ) {
    if (enable) {
      const defaultBudget = this.rtbCampaigns
        ? this.rtbCampaigns.reduce((acc, rtbCampaign) => {
            return (
              acc +
              Math.max(
                +_.defaultTo(rtbCampaign.budget, 0),
                this.campaignManager.getMinBudgetOfCampaign(
                  rtbCampaign,
                  this.minBudgetPerDay
                )
              )
            );
          }, 0)
        : 0;
      valuesUpdater((prev) => ({
        ...prev,
        budget: defaultBudget,
        rtb: {
          ...defaultTo(prev.rtb, {}),
          bid_strategy: this.getDefaultBidStrategy(),
          campaign_budgets: undefined
        }
      }));
    } else {
      if (
        this.rtbCampaigns &&
        this.rtbCampaigns.length > 0 &&
        _.get(this.l1Object, 'autoOptimise')
      ) {
        showManagementModal();
      } else {
        valuesUpdater((prev) => ({
          ...prev,
          budget: _.get(this.l1Object, 'budget', prev.budget),
          rtb: {
            ...defaultTo(prev.rtb, {}),
            bid_strategy: undefined
          }
        }));
      }
    }
  }

  showRemainBudget(_values: Partial<L1Object>) {
    return true;
  }

  toChannelNativeData(l1Object: L1Object, _extraData: any) {
    const rtb = l1Object.rtb
      ? {
          ...l1Object.rtb,
          bid_strategy: l1Object.rtb.bid_strategy
            ? l1Object.rtb.bid_strategy
            : null
        }
      : undefined;
    const pmax3Objective = l1Object.objective;
    const objective = this.l1ObjectManager.getNativeObjectiveValue(
      l1Object.channel,
      pmax3Objective
    );
    return {
      ...l1Object,
      objective,
      l1ObjectLifetimeBudget: _.isNil(l1Object.budget) ? 0 : l1Object.budget,
      rtb
    };
  }
}
